/**
 * Tokens do Senior Design System (SDS).
 *
 * Fontes:
 *  - @seniorsistemas/tailwind-config@1.1.0  (dist/tokens/colors.js, radius.js, screens.js)
 *  - @seniorsistemas/primeng-theme@10.2.4   (dist/scss/_colors.scss, components/*.scss)
 *  - Documentação SDS no zeroheight (escala tipográfica, grid, regras de texto sobre cor)
 *
 * NÃO edite valores aqui sem conferir a fonte. Não adicione cores que não estejam
 * na paleta oficial: se precisar de uma cor nova, isso é decisão do time de design.
 */

// ---------------------------------------------------------------------------
// Cores principais
// ---------------------------------------------------------------------------

export const sdsColors = {
  primary: '#428BCA',
  secondary: '#7892A1',

  // Marca
  senior: '#008267',
  seniorX: '#FFAF00',
  seniorXSecondary: '#FF8200',

  // Criticidade / feedback
  success: '#0C9348',
  info: '#428BCA',
  warning: '#FCBF10',
  danger: '#F8931F',
  error: '#C13018',

  // Texto
  textPrimary: '#333333',
  textSecondary: '#999999',
  textLight: '#FFFFFF',

  // Bordas e superfícies
  /**
   * `$default-primary-color` do tema (#CCCCCC). É a cor de CHIP/token:
   * `components/tokens.scss` aplica `background-color: #ccc` e
   * `border: 1px solid #ccc`.
   *
   * NÃO é a borda de campo de texto — esse é o erro fácil de cometer aqui.
   * O input do tema usa `grayscale-30` (#C1C1CC), literal em
   * `components/inputtext.scss:3` e `autocomplete.scss:8`. Para borda de campo
   * use `sdsGrayscale[30]`, não este token.
   *
   * As variáveis `$input-border-color` e `$input-hover-border-color`
   * (`_colors.scss:57-58`) apontam para #CCCCCC, mas NENHUM componente do
   * pacote as consome — são variáveis mortas. Não as trate como spec de input.
   */
  borderChip: '#CCCCCC',
  borderTable: '#DDDDDD',

  // Estados
  hover: '#DEDCE5',
  selection: '#EEEBF2',
  tableHover: '#EBEBEB',
  chipHover: '#E5EAEA',
  focusRing: '#22DCE6',
} as const;

/** Escala de cinzas: grayscale-100 (mais escuro) a grayscale-0 (branco). */
export const sdsGrayscale = {
  100: '#0E1119',
  90: '#212533',
  80: '#383E4C',
  70: '#525966',
  60: '#6E7280',
  50: '#888B99',
  40: '#A5A5B2',
  30: '#C1C1CC',
  20: '#DEDCE5',
  10: '#EEEBF2',
  5: '#FBFAFC',
  0: '#FFFFFF',
} as const;

/**
 * Escalas de base completas: 6 tons por cor (900/700/500/300/100/50).
 * O tom 500 é o principal; o 50 é usado como fundo de mensagem.
 *
 * Regras oficiais de cor de texto sobre cada tom estão em
 * `references/color-tokens.md` (seção "Regras de texto sobre cor") — não
 * derive por conta própria, porque cinco tons não atingem 4,5:1 com nenhuma
 * das duas cores de texto e precisam ficar restritos a texto grande.
 *
 * ATENÇÃO: `sunsetOrange[500]` é `#F45B5B` (valor do tailwind-config), mas a
 * paleta de gráficos usa `#EB5B5C` (valor do primeng-theme). A divergência é
 * real e documentada — não unifique os dois. Para gráficos, use
 * `sdsChartColors`.
 */
export const sdsBaseScales = {
  lime:         { 900: '#436500', 700: '#6DA300', 500: '#ADE500', 300: '#C8FF5C', 100: '#E6FFB3', 50: '#F7FFE7' },
  hippieBlue:   { 900: '#1D2F34', 700: '#426E78', 500: '#67ACBC', 300: '#9ECAD4', 100: '#D5E8EC', 50: '#F1F7F8' },
  teal:         { 900: '#0C2827', 700: '#005050', 500: '#008080', 300: '#4DA6A6', 100: '#B3D9D9', 50: '#E0F0F0' },
  tarawera:     { 900: '#021017', 700: '#042534', 500: '#063951', 300: '#608190', 100: '#BBC9CF', 50: '#E8EDEF' },
  clairvoyant:  { 900: '#060108', 700: '#290535', 500: '#400753', 300: '#856191', 100: '#CABBD0', 50: '#EDE8EF' },
  cocoaBean:    { 900: '#270B0A', 700: '#401817', 500: '#50201F', 300: '#856362', 100: '#CBBCBC', 50: '#EAE4E4' },
  sunsetOrange: { 900: '#431919', 700: '#9C3A3A', 500: '#F45B5B', 300: '#F89696', 100: '#FCD2D2', 50: '#FEF0F0' },
  buttercup:    { 900: '#EE7708', 700: '#F29112', 500: '#F5A319', 300: '#F8BF5E', 100: '#FCE3BA', 50: '#FEF4E3' },
} as const;

/**
 * Fundos de mensagem. Correspondem aos tons -50 das escalas de base.
 * O fundo de sucesso é um tom de teal, não de verde — parece inconsistente
 * mas é o valor oficial.
 */
export const sdsMessageBg = {
  success: '#E0F0F0',
  info: '#F1F7F8',
  warning: '#FEF4E3',
  error: '#FEF0F0',
} as const;

/**
 * Paleta de gráficos, NA ORDEM OFICIAL. Use sequencialmente por série.
 * Se houver mais séries que cores, repita a sequência.
 *
 * Para Stats/totalizadores, a primeira cor é Lime 700 (#6DA300) em vez de
 * Lime 500 — use `sdsStatColors`.
 */
export const sdsChartColors = [
  '#ADE500', // lime
  '#67ACBC', // hippie-blue
  '#008080', // teal
  '#063951', // tarawera
  '#400753', // clairvoyant
  '#50201F', // cocoa-bean
  '#EB5B5C', // sunset-orange (valor do tema; tailwind traz #F45B5B)
  '#F5A319', // buttercup
] as const;

export const sdsStatColors = ['#6DA300', ...sdsChartColors.slice(1)] as const;

/**
 * Cor de texto a usar sobre cada cor de gráfico (rótulo dentro da barra/fatia).
 * Derivado das regras oficiais de variação tonal do SDS.
 */
export const sdsChartTextColor: Record<string, string> = {
  '#ADE500': sdsColors.textPrimary,
  '#67ACBC': sdsColors.textPrimary,
  '#008080': sdsColors.textLight,
  '#063951': sdsColors.textLight,
  '#400753': sdsColors.textLight,
  '#50201F': sdsColors.textLight,
  '#EB5B5C': sdsColors.textPrimary, // 3,72:1 — só rótulo grande
  '#F5A319': sdsColors.textPrimary, // documentação: preto em todas as variações
  '#6DA300': sdsColors.textLight,   // regra oficial; 3,05:1, só rótulo grande — ver nota abaixo
};

/**
 * Nota sobre `#6DA300` (lime-700): a regra oficial do SDS manda texto branco,
 * que dá 3,05:1. `#333333` sobre a mesma cor dá 4,14:1 — melhor, mas ainda
 * abaixo dos 4,5:1 de texto normal. Nenhuma das duas resolve, e por isso o
 * lime-700 é uma das cinco exceções que a documentação reconhece.
 *
 * O mapa acima segue a regra oficial e não deve ser alterado. Quem precisa de
 * rótulo pequeno dentro de barra ou fatia usa `readableTextOn`, em
 * `src/pages/home/charts/chartColors.ts`: ele segue este mapa enquanto a regra
 * atinge 4,5:1 e, nas exceções, escolhe a cor de maior contraste.
 */

// ---------------------------------------------------------------------------
// Radius
// ---------------------------------------------------------------------------

export const sdsRadius = {
  xxsmall: 1,
  xsmall: 2,
  // 3px existe na escala do tailwind-config, mas NÃO é o radius de campo: no
  // primeng-theme só autocomplete (com !important) e checkbox usam 3px.
  small: 3,   // autocomplete, checkbox
  // Campos de texto entram aqui: inputtext, dropdown, select e multi-select
  // usam 4px no tema.
  medium: 4,  // inputs, botões, cards, mensagens, chips
  xbig: 6,
  xxbig: 10,
  huge: 15,
  /** .sds-rounded do tema: badges e chips-token */
  rounded: 16,
} as const;

// ---------------------------------------------------------------------------
// Breakpoints e grid
// ---------------------------------------------------------------------------

export const sdsBreakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1360,
  big: 1600,
} as const;

/** Colunas, margem e calha por breakpoint. `+` na doc = valor mínimo. */
export const sdsGrid = {
  xs: { columns: 4, margin: 16, gutter: 16 },
  sm: { columns: 8, margin: 16, gutter: 16 },
  md: { columns: 8, margin: 24, gutter: 24 },
  lg: { columns: 12, margin: 32, gutter: 24 },
  xl: { columns: 12, margin: 24, gutter: 16 },
  xxl: { columns: 12, margin: 32, gutter: 32 },
  big: { columns: 12, margin: 32, gutter: 32 },
} as const;

/** Resolução mínima suportada pelo SDS. 320px apenas se houver suporte mobile. */
export const sdsMinResolution = { desktop: 1280, mobile: 320 } as const;

/** Método de espaçamento: múltiplos de 8, com meio-passo de 4. */
export const sdsSpacingUnit = 8;

// ---------------------------------------------------------------------------
// Dimensões de componente
// ---------------------------------------------------------------------------

export const sdsSizes = {
  /** Altura de input, select e botão. */
  inputHeight: 35,
  buttonMinWidth: 80,
  iconButtonMinWidth: 32,
  tabMinHeight: 44,
  tableCellPadding: 15,
  messageMinHeight: 70,
  messagePadding: 15,
  messageBorderWidth: 5,
  badgeHeight: 20,
  badgeMaxWidth: 160,
  tabIndicatorHeight: 4,
} as const;

// ---------------------------------------------------------------------------
// Tipografia
// ---------------------------------------------------------------------------

/**
 * Escala oficial: 8 estilos nomeados, 4 tamanhos.
 * Invariantes: line-height 150% e letter-spacing 0 em TODOS os estilos.
 */
export const sdsFontFamily = '"Open Sans", sans-serif';
export const sdsLineHeight = 1.5;

export const sdsTypeScale = {
  pageTitle: { fontSize: '22px', fontWeight: 400 },
  panelTitle: { fontSize: '16px', fontWeight: 400 },
  sectionTitle: { fontSize: '14px', fontWeight: 700 },
  cardsTitle: { fontSize: '14px', fontWeight: 700 },
  body: { fontSize: '14px', fontWeight: 400 },
  labels: { fontSize: '14px', fontWeight: 400 },
  inputs: { fontSize: '14px', fontWeight: 400 },
  helper: { fontSize: '12px', fontWeight: 400 },
} as const;

/** Pesos oficiais de Open Sans no SDS. Não use 300, 500 ou 800. */
export const sdsFontWeights = { regular: 400, semibold: 600, bold: 700 } as const;
